/**
 * Sticky Add to Cart Component
 * Makes the add to cart button sticky on mobile when scrolled past,
 * and gracefully returns it to normal position when scrolled back into view
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.productForm = null;
    this.originalParent = null;
    this.originalNextSibling = null;
    this.stickyContainer = null;
    this.observer = null;
    this.isSticky = false;
    this.isMobile = window.innerWidth < 990;
  }

  connectedCallback() {
    this.init();
    this.setupResizeObserver();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.resizeObserver) {
      window.removeEventListener('resize', this.resizeObserver);
    }
  }

  init() {
    // Only enable on mobile
    if (!this.isMobile) return;

    // Find the buy buttons block - look for product-form element which is inside the buy_buttons block
    const productFormElement = this.querySelector('product-form');

    if (!productFormElement) {
      console.warn('Sticky Add to Cart: Could not find product-form element');
      return;
    }

    // Get the parent div.product-form-block which wraps the product-form
    this.productForm = productFormElement.closest('.product-form-block');

    if (!this.productForm) {
      console.warn('Sticky Add to Cart: Could not find product-form-block wrapper');
      return;
    }

    // Find the price block
    this.priceBlock = this.querySelector('[data-update-id*="price"]');

    // Store original position
    this.originalParent = this.productForm.parentElement;
    this.originalNextSibling = this.productForm.nextElementSibling;

    // Create sticky container
    this.createStickyContainer();

    // Setup Intersection Observer
    this.setupObserver();
  }

  createStickyContainer() {
    this.stickyContainer = document.createElement('div');
    this.stickyContainer.className = 'sticky-add-to-cart-container';
    this.stickyContainer.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      padding: 15px 20px;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
      z-index: 100;
      transform: translateY(100%);
      transition: transform 0.3s ease-in-out;
      display: none;
    `;

    // Insert into body
    document.body.appendChild(this.stickyContainer);
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '-100px 0px 0px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && !this.isSticky) {
          // Element scrolled out of view - make sticky
          this.makeSticky();
        } else if (entry.isIntersecting && this.isSticky) {
          // Element scrolled back into view - restore to normal
          this.restoreToNormal();
        }
      });
    }, options);

    this.observer.observe(this.productForm);
  }

  makeSticky() {
    if (this.isSticky || !this.isMobile) return;

    this.isSticky = true;

    // Create a wrapper for price and button layout
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'sticky-atc-content';

    // Clone the price block if it exists
    if (this.priceBlock) {
      const priceClone = this.priceBlock.cloneNode(true);
      priceClone.className = 'sticky-atc-price';
      contentWrapper.appendChild(priceClone);
    }

    // Clone the form block
    const formClone = this.productForm.cloneNode(true);
    formClone.className = 'sticky-atc-form';
    contentWrapper.appendChild(formClone);

    // Move to sticky container
    this.stickyContainer.appendChild(contentWrapper);
    this.stickyContainer.style.display = 'block';

    // Animate in
    requestAnimationFrame(() => {
      this.stickyContainer.style.transform = 'translateY(0)';
    });

    // Re-initialize any form functionality on the cloned element
    this.reinitializeFormListeners(formClone);
  }

  restoreToNormal() {
    if (!this.isSticky) return;

    this.isSticky = false;

    // Animate out
    this.stickyContainer.style.transform = 'translateY(100%)';

    // Remove after animation
    setTimeout(() => {
      this.stickyContainer.innerHTML = '';
      this.stickyContainer.style.display = 'none';
    }, 300);
  }

  reinitializeFormListeners(clone) {
    // Find the product-form custom element in the clone
    const productFormElement = clone.querySelector('product-form');
    if (productFormElement) {
      // Trigger a re-initialization if the component has a method for it
      if (typeof productFormElement.onVariantChange === 'function') {
        // Connect to the original form's variant changes
        const originalForm = this.productForm.querySelector('product-form');
        if (originalForm) {
          // Copy current state
          const variantInput = originalForm.querySelector('input[name="id"]');
          const clonedVariantInput = productFormElement.querySelector('input[name="id"]');
          if (variantInput && clonedVariantInput) {
            clonedVariantInput.value = variantInput.value;
          }
        }
      }
    }

    // Handle form submission from sticky container
    const form = clone.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        // Submit the original form instead
        const originalForm = this.productForm.querySelector('form[data-type="add-to-cart-form"]');
        if (originalForm) {
          const submitButton = originalForm.querySelector('button[type="submit"]');
          if (submitButton) {
            submitButton.click();
          }
        }
      });
    }
  }

  setupResizeObserver() {
    this.resizeObserver = () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 990;

      // If switching between mobile and desktop
      if (wasMobile !== this.isMobile) {
        if (!this.isMobile && this.isSticky) {
          // Switched to desktop - restore to normal
          this.restoreToNormal();
        } else if (this.isMobile && this.observer) {
          // Switched to mobile - reinitialize
          this.observer.disconnect();
          this.setupObserver();
        }
      }
    };

    window.addEventListener('resize', this.resizeObserver);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
